import React from "react";
import PropTypes from "prop-types";
import validator from "validate.js";
import NotificationBox from "components/elements/notification-box";
import SVG from "components/elements/svg";
import TextContent from "components/elements/text-content";
import ValidatedInput from "components/elements/validated-input";
import { FORM_RULES } from "components/register/form.validation";
import { birthDays, birthMonths, birthYears } from "helpers/date-of-birth";
import getFormValues from "helpers/get-form-values";
import PHONE_PREFIXES from "helpers/phone-prefixes";
import scrollToElem from "helpers/scroll-to-elem";
import API from "services/api";
import Checkbox from "../elements/checkbox";
import Preloader from "../elements/preloader";

class SignUpForm extends React.Component {
  constructor(props) {
    super(props);
    this.api = new API();
    this.state = {
      accountType: props.form.accountType || props.defaultAccountType || null,
      validationErrors: {},
      error: {},
      showForm:
        (props.form.accountType || props.defaultAccountType || null) ===
        "buyer",
    };
  }

  componentDidMount() {
    this.api
      .get("/ip-check", {})
      .then((response) => {
        if (response.success) {
          this.setState({
            ipError: false,
          });
        }
      })
      .catch((error) => {
        this.setState({
          ipError: {
            type: "danger",
            message: error.message,
          },
        });
      });
  }

  componentDidUpdate(prevProps, prevState) {
    const { accountType: newAccountType } = this.state;
    const { accountType: prevAccountType } = prevState;

    if (newAccountType !== prevAccountType) {
      window.history.replaceState({}, "", `/register/${newAccountType}`);
      scrollToElem(this.formContainer);
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    const data = getFormValues(e.target);
    const validationErrors = validator(data, FORM_RULES, {
      fullMessages: false,
    });
    const form = { ...data, accountType: this.state.accountType };

    if (validationErrors) {
      this.setState({ validationErrors });
      return true;
    }

    this.api
      .post("/users/register/validate", form)
      .then(() => {
        this.props.onStepChange({
          step: 2,
          form,
        });
      })
      .catch((error) => {
        if (error.data && error.data.errors) {
          this.setState({
            validationErrors: error.data.errors,
          });
        } else {
          this.setState({
            error: {
              type: "danger",
              message: error.message,
            },
          });
          scrollToElem(this.formContainer);
        }
      });

    return true;
  }

  handleInputFocus(e, name) {
    const { validationErrors } = this.state;
    delete validationErrors[name];
    this.setState({ validationErrors });
  }

  handleInputBlur(e, name) {
    if (!FORM_RULES[name]) {
      return;
    }

    let errors = [];

    if (name === "passwordConfirm") {
      errors = validator(
        {
          password: this.password.value,
          passwordConfirm: e.target.value,
        },
        {
          password: FORM_RULES.password,
          passwordConfirm: FORM_RULES.passwordConfirm,
        },
        { fullMessages: false },
      );
      errors = errors ? errors.passwordConfirm : [];
    } else if (name === "emailConfirm") {
      errors = validator(
        {
          email: this.email.value,
          emailConfirm: e.target.value,
        },
        {
          email: FORM_RULES.email,
          emailConfirm: FORM_RULES.emailConfirm,
        },
        { fullMessages: false },
      );
      errors = errors ? errors.emailConfirm : [];
    } else {
      errors = validator.single(e.target.value, FORM_RULES[name]);
    }

    if (errors) {
      const validationErrors = {
        ...this.state.validationErrors,
        [name]: errors,
      };
      this.setState({ validationErrors });
    } else if (name === "email") {
      this.api
        .post("/users/register/validate", { email: e.target.value })
        .catch((error) => {
          const validationErrors = {
            ...this.state.validationErrors,
            ...error.data.errors,
          };
          this.setState({ validationErrors });
        });
    }
  }

  render() {
    const {
      error: serverError,
      submitting,
      accountType,
      validationErrors,
      ipError,
      showForm,
    } = this.state;
    const { error, form } = this.props;

    if (ipError === undefined) {
      return <Preloader show />;
    }
    if (!!ipError && ipError.type && ipError.message) {
      return (
        <div className="container">
          <NotificationBox type={ipError.type}>
            {ipError.message}
          </NotificationBox>
        </div>
      );
    }

    return (
      <div className="register-form">
        <form onSubmit={(e) => this.handleSubmit(e)} noValidate>
          <div className="accept-terms">
            <label htmlFor="acceptTerms">Accept rules</label>
            <input type="checkbox" name="acceptTerms" id="acceptTerms" />
          </div>

          <section className="register-form__account-type">
            <div className="container">
              <h2 className="section-heading">Choose an account type below</h2>

              <div className="row">
                <div className="col-md-6 col-xs-12">
                  <div
                    className={`box${accountType === "buyer" ? " active" : ""}`}
                    onClick={() =>
                      this.setState({ accountType: "buyer", showForm: true })
                    }
                    role="button"
                    tabIndex="0"
                  >
                    <div className="tick">
                      <SVG src="check" className="icon" />
                    </div>

                    <div className="content">
                      <p className="title">Buyer account</p>

                      <TextContent>
                        <p>
                          A buyer’s account allows you to browse and bid on
                          multiple properties.
                        </p>
                      </TextContent>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xs-12">
                  <div
                    className={`box${
                      accountType === "seller" ? " active" : ""
                    }`}
                    onClick={() =>
                      this.setState({ accountType: "seller", showForm: false })
                    }
                    role="button"
                    tabIndex="0"
                  >
                    <div className="tick">
                      <SVG src="check" className="icon" />
                    </div>

                    <div className="content">
                      <p className="title">Seller account</p>

                      <TextContent>
                        <p>
                          This type of account is designed for a one-off
                          property sale only. In order to get your property
                          published through Hammer Price Homes it must first be
                          approved by us. This will take approximately 2 working
                          days.
                        </p>
                      </TextContent>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {accountType && (
            <section
              className="register-form__content"
              ref={(input) => {
                this.formContainer = input;
              }}
            >
              <div className="container">
                <NotificationBox type={error.type || serverError.type}>
                  {error.message || serverError.message}
                </NotificationBox>

                {accountType === "seller" && (
                  <NotificationBox type="info">
                    <p>
                      <b>Are you sure you meant to register as a seller?</b>
                    </p>
                    <p>
                      If you do wish to sell a property then before registering
                      please call our sales team on &nbsp;
                      <a href="tel:02038141888">020 3814 1888</a>&nbsp; to
                      ensure your property is right for our method of sale.
                    </p>

                    <Checkbox
                      label="I am sure"
                      name="showForm"
                      onChange={(e) =>
                        this.setState({ showForm: e.target.checked })
                      }
                    />
                  </NotificationBox>
                )}

                {showForm && (
                  <div>
                    <div className="row">
                      <div className="col-md-3 col-sm-6 col-xs-12">
                        <ValidatedInput errors={validationErrors.firstName}>
                          <label className="required" htmlFor="firstName">
                            First name
                          </label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.firstName}
                            required
                          />
                        </ValidatedInput>
                      </div>

                      <div className="col-md-3 col-sm-6 col-xs-12">
                        <ValidatedInput errors={validationErrors.lastName}>
                          <label className="required" htmlFor="lastName">
                            Last name
                          </label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.lastName}
                            required
                          />
                        </ValidatedInput>
                      </div>

                      <div className="col-md-6 col-xs-12">
                        <p className="form-label required">Date of birth</p>
                        <div className="row">
                          <div className="col-xs-4 inline-column">
                            <ValidatedInput errors={validationErrors.birthDay}>
                              <label
                                htmlFor="birthDay"
                                className="visuallyhidden"
                              >
                                Day of birth
                              </label>
                              <select
                                id="birthDay"
                                name="birthDay"
                                className="form-control"
                                onFocus={(e) =>
                                  this.handleInputFocus(e, e.target.name)
                                }
                                onBlur={(e) =>
                                  this.handleInputBlur(e, e.target.name)
                                }
                                defaultValue={form.birthDay || "Day"}
                                required
                              >
                                {birthDays()}
                              </select>
                            </ValidatedInput>
                          </div>

                          <div className="col-xs-4 inline-column">
                            <ValidatedInput
                              errors={validationErrors.birthMonth}
                            >
                              <label
                                htmlFor="birthMonth"
                                className="visuallyhidden"
                              >
                                Month of birth
                              </label>
                              <select
                                id="birthMonth"
                                name="birthMonth"
                                className="form-control"
                                onFocus={(e) =>
                                  this.handleInputFocus(e, e.target.name)
                                }
                                onBlur={(e) =>
                                  this.handleInputBlur(e, e.target.name)
                                }
                                defaultValue={form.birthMonth || "Month"}
                                required
                              >
                                {birthMonths()}
                              </select>
                            </ValidatedInput>
                          </div>

                          <div className="col-xs-4 inline-column">
                            <ValidatedInput errors={validationErrors.birthYear}>
                              <label
                                htmlFor="birthYear"
                                className="visuallyhidden"
                              >
                                Year of birth
                              </label>
                              <select
                                id="birthYear"
                                name="birthYear"
                                className="form-control"
                                onFocus={(e) =>
                                  this.handleInputFocus(e, e.target.name)
                                }
                                onBlur={(e) =>
                                  this.handleInputBlur(e, e.target.name)
                                }
                                defaultValue={form.birthYear || "Year"}
                                required
                              >
                                {birthYears()}
                              </select>
                            </ValidatedInput>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-3 col-sm-6 col-xs-12">
                        <ValidatedInput errors={validationErrors.email}>
                          <label className="required" htmlFor="email">
                            E-mail address
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.email}
                            ref={(input) => {
                              this.email = input;
                            }}
                            required
                          />
                        </ValidatedInput>
                      </div>

                      <div className="col-md-3 col-sm-6 col-xs-12">
                        <ValidatedInput errors={validationErrors.emailConfirm}>
                          <label className="required" htmlFor="emailConfirm">
                            Confirm e-mail address
                          </label>
                          <input
                            type="email"
                            id="emailConfirm"
                            name="emailConfirm"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.emailConfirm}
                            required
                          />
                        </ValidatedInput>
                      </div>

                      <div className="col-md-3 col-sm-6 col-xs-12">
                        <ValidatedInput errors={validationErrors.password}>
                          <label className="required" htmlFor="password">
                            Password
                          </label>
                          <input
                            type="password"
                            id="password"
                            name="password"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            minLength="6"
                            defaultValue={form.password}
                            ref={(input) => {
                              this.password = input;
                            }}
                            required
                          />
                        </ValidatedInput>
                      </div>

                      <div className="col-md-3 col-sm-6 col-xs-12">
                        <ValidatedInput
                          errors={validationErrors.passwordConfirm}
                        >
                          <label className="required" htmlFor="passwordConfirm">
                            Confirm password
                          </label>
                          <input
                            type="password"
                            id="passwordConfirm"
                            name="passwordConfirm"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            minLength="6"
                            defaultValue={form.passwordConfirm}
                            required
                          />
                        </ValidatedInput>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-6">
                        <ValidatedInput errors={validationErrors.address1}>
                          <label className="required" htmlFor="address1">
                            Address line 1
                          </label>
                          <input
                            type="text"
                            id="address1"
                            name="address1"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.address1}
                            required
                          />
                        </ValidatedInput>
                      </div>
                      <div className="col-sm-6">
                        <ValidatedInput errors={validationErrors.address2}>
                          <label htmlFor="address1">Address line 2</label>
                          <input
                            type="text"
                            id="address2"
                            name="address2"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.address2}
                          />
                        </ValidatedInput>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-3">
                        <ValidatedInput errors={validationErrors.city}>
                          <label className="required" htmlFor="city">
                            Town / City
                          </label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.city}
                            required
                          />
                        </ValidatedInput>
                      </div>

                      <div className="col-sm-3">
                        <ValidatedInput errors={validationErrors.postCode}>
                          <label className="required" htmlFor="postCode">
                            Postcode
                          </label>
                          <input
                            type="text"
                            id="postCode"
                            name="postCode"
                            className="form-control"
                            onFocus={(e) =>
                              this.handleInputFocus(e, e.target.name)
                            }
                            onBlur={(e) =>
                              this.handleInputBlur(e, e.target.name)
                            }
                            defaultValue={form.postCode}
                            required
                          />
                        </ValidatedInput>
                      </div>

                      <div className="col-sm-6">
                        <p className="form-label required">Contact number</p>
                        <div className="row">
                          <div className="col-xs-5 col-sm-4 inline-column">
                            <ValidatedInput
                              errors={validationErrors.phonePrefix}
                            >
                              <label
                                htmlFor="phonePrefix"
                                className="visuallyhidden"
                              >
                                Contact number prefix
                              </label>
                              <select
                                id="phonePrefix"
                                name="phonePrefix"
                                className="form-control"
                                onFocus={(e) =>
                                  this.handleInputFocus(e, e.target.name)
                                }
                                onBlur={(e) =>
                                  this.handleInputBlur(e, e.target.name)
                                }
                                defaultValue={form.phonePrefix}
                                required
                              >
                                <option value="44" key="UK (+44)">
                                  UK (+44)
                                </option>
                                <optgroup label="Other countries">
                                  {PHONE_PREFIXES.map((item) => (
                                    <option value={item.value} key={item.label}>
                                      {item.label}
                                    </option>
                                  ))}
                                </optgroup>
                              </select>
                            </ValidatedInput>
                          </div>

                          <div className="col-xs-7 col-sm-8 inline-column">
                            <ValidatedInput errors={validationErrors.phone}>
                              <label htmlFor="phone" className="visuallyhidden">
                                Phone number
                              </label>
                              <input
                                type="tel"
                                id="phone"
                                name="phone"
                                className="form-control"
                                onFocus={(e) =>
                                  this.handleInputFocus(e, e.target.name)
                                }
                                onBlur={(e) =>
                                  this.handleInputBlur(e, e.target.name)
                                }
                                defaultValue={form.phone}
                                required
                              />
                            </ValidatedInput>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {showForm && (
                  <div className="actions">
                    <button
                      type="submit"
                      className={`btn btn-primary with-arrow ${
                        submitting ? "submitting" : ""
                      }`}
                    >
                      <span>Next step</span>
                      <SVG src="arrow-right" className="arrow" />
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}
        </form>
      </div>
    );
  }
}

SignUpForm.propTypes = {
  onStepChange: PropTypes.func.isRequired,
  defaultAccountType: PropTypes.oneOf(["seller", "buyer"]),
  form: PropTypes.shape({
    accountType: PropTypes.string,
  }).isRequired,
  error: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
  }),
};

SignUpForm.defaultProps = {
  error: {
    type: null,
    message: null,
  },
  defaultAccountType: null,
};

export default SignUpForm;
